import {Box} from '@indoqa/style-system'
import {IStyle} from 'fela'
import * as React from 'react'
import {useFela} from 'react-fela'
import {Theme} from '../../../app/theme'
import {Button, Type} from '../../../commons/components/button/Button'
import {digitalArfbrLoginPage} from '../../../digital/store/digital.paths'
import {arPathContentResources} from '../../store/ar.paths'
import {ArAnsprechpartnerSection} from '../contact/ArAnsprechpartnerSection'
import {ArLink} from '../links/ArLink'
import {ArDivider} from './ArDivider'

const TESTZUGANG_LINK = '/arbeitsrecht-testzugang'
const PRODUKT_LINK = 'https://arbeitsrecht-fuer-betriebsraete.at/'
const KIARA_PREVIEW_WIDTH = 440

export const ArOverviewMarketingPanel = () => {
  const {css, theme} = useFela<Theme>()

  // Text and image are stacked on mobile and split 50:50 from tablet up.
  const highlightStyle: IStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing.space6,
    tablet: {
      gridTemplateColumns: '1fr 1fr',
      alignItems: 'center',
    },
  }
  const kiaraPreviewColumnStyle: IStyle = {
    display: 'flex',
    justifyContent: 'center',
  }
  // The global `img` rule in DigitalCSSRules centers every image at 80% width, which has to be
  // undone here.
  const kiaraPreviewStyle: IStyle = {
    width: '100%',
    maxWidth: KIARA_PREVIEW_WIDTH,
    margin: 0,
  }
  const eyebrowStyle: IStyle = {
    color: theme.colors.textReduced,
  }
  const testButtonStyle: IStyle = {
    textAlign: 'center',
    tablet: {
      textAlign: 'left',
    },
  }

  return (
    <Box fullWidth mt={8} mb={20}>
      <h1>Arbeitsrecht für Betriebsräte</h1>
      <p>
        Als Betriebsrät:in hast du oft keine Zeit, dich durch stapelweise Fachliteratur zu arbeiten.{' '}
        <strong>Arbeitsrecht für Betriebsräte</strong> bringt das umfangreiche Arbeitsrechtswissen des ÖGB-Verlags auf
        eine digitale Plattform – durchsuchbar, filterbar und immer am aktuellen Rechtsstand. Du findest in Sekunden,
        was du für deine Betriebsratsarbeit brauchst.
      </p>
      <p>
        <ArLink name="Alle Inhalte" link={arPathContentResources()} bold /> werden laufend aktualisiert, damit du deine
        arbeitsrechtlichen Fragen mithilfe des Fachwissens des ÖGB-Verlags immer rechtssicher beantworten kannst.
      </p>

      <Box pt={8}>
        <h2>Ein Team: Arbeitsrecht für Betriebsräte + KIARA</h2>
      </Box>
      <p>
        Jetzt kannst du dieses Wissen auch direkt befragen. Mit <strong>KIARA, dem KI-Chatbot des ÖGB-Verlags.</strong>{' '}
        Ob Urlaubsanspruch, Kündigungsfrist, Arbeitszeit oder Mitbestimmung: der integrierte KI-Chatbot KIARA findet die
        relevanten Informationen in der Fachliteratur des ÖGB-Verlags und liefert dir{' '}
        <strong>Antworten, auf die du dich verlassen kannst</strong>.
      </p>

      <ArDivider />
      <Box style={highlightStyle}>
        <Box>
          <Box<Theme> fontStyle="brandBold" style={eyebrowStyle}>
            VERSTÄRKUNG FÜR DEIN TEAM
          </Box>
          <Box pt={2}>
            <h3>Du fragst, KIARA liefert die Antwort</h3>
          </Box>
          <Box py={2}>
            🗨️ <strong>Einfach fragen:</strong>
            <br /> Du formulierst dein Anliegen so, wie du es auch einer Kollegin oder einem Kollegen schildern würdest.
          </Box>
          <Box py={2}>
            ⚡ <strong>Schnell Antworten finden:</strong>
            <br /> KIARA durchsucht die Fachinhalte des ÖGB-Verlags und bringt das relevante Wissen auf den Punkt.
          </Box>
          <Box py={2}>
            📖 <strong>Sicher argumentieren:</strong>
            <br /> Jede Antwort enthält die passende Quellenangabe, damit du nachvollziehen kannst, worauf du dich
            stützt.
          </Box>
        </Box>
        <Box style={kiaraPreviewColumnStyle}>
          <img
            src={`${process.env.PUBLIC_URL}/kiara-preview.gif`}
            alt="Screenshot des KIARA-Chats"
            className={css(kiaraPreviewStyle)}
          />
        </Box>
      </Box>
      <ArDivider />

      <Box>
        <h2>Interessiert? Jetzt kostenlos und unverbindlich testen</h2>
      </Box>
      <p>
        Du möchtest erst sehen, ob Arbeitsrecht für Betriebsräte das Richtige für dein Gremium ist? Kein Problem. Teste
        das Tool kostenlos und unverbindlich, ohne versteckte Kosten und ohne automatische Verlängerung.
      </p>

      <Box pt={4} style={testButtonStyle}>
        <Button type={Type.CTA} linkTo={TESTZUGANG_LINK} text="JETZT KOSTENLOS TESTEN" />
      </Box>
      <Box pt={6}>
        Bereits Kund:in? <ArLink name="Jetzt einloggen" link={digitalArfbrLoginPage()} bold />
      </Box>

      <Box pt={12}>
        <h2>Fragen zum Produkt?</h2>
      </Box>
      <p>
        Du möchtest mehr über die Funktionen und Vorteile von <b>Arbeitsrecht für Betriebsräte</b> und KIARA erfahren?
        Auf <ArLink name="arbeitsrecht-fuer-betriebsraete.at" link={PRODUKT_LINK} bold /> findest du alle Details,
        Antworten auf häufige Fragen sowie Informationen zu Preisen und Paketen.
      </p>

      <ArAnsprechpartnerSection intro="Buche einen Termin mit deinem persönlichen Ansprechpartner und lasse dir die Möglichkeiten von Arbeitsrecht für Betriebsräte und KIARA zeigen." />
    </Box>
  )
}
